import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import tiktokService, { TikTokApiError } from '../services/tiktok.service';
import { useAppFeatures } from './AppFeaturesContext';
import { useAuth } from './AuthContext';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/safeStorage';
import type { TikTokInstanceOut } from '../types/tiktok.types';

// TikTokContext — список TikTok-каналов организации + выбранный инстанс.
// Паттерн InstagramContext: грузим listInstances при наличии фичи, выбор персистим.
// Приоритет: предыдущий выбранный (если жив) → сохранённый в localStorage → первый.

const STORAGE_KEY = 'tiktok_selected_instance_id';

interface TikTokContextType {
  instances: TikTokInstanceOut[];
  currentInstance: TikTokInstanceOut | null;
  isLoading: boolean;
  error: string | null;
  setCurrentInstance: (instance: TikTokInstanceOut | null) => void;
  refreshInstances: () => Promise<void>;
}

const Ctx = createContext<TikTokContextType | null>(null);

export const TikTokProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const features = useAppFeatures();
  const { isAuthenticated } = useAuth();
  const [instances, setInstances] = useState<TikTokInstanceOut[]>([]);
  const [currentInstance, setCurrentInstanceState] = useState<TikTokInstanceOut | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshInstances = useCallback(async () => {
    if (!isAuthenticated || !features.tiktok) {
      setInstances([]);
      setCurrentInstanceState(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const list = await tiktokService.listInstances();
      setInstances(list);
      setCurrentInstanceState((prev) => {
        const storedId = Number(safeGetItem(STORAGE_KEY));
        return (
          list.find((i) => i.id === prev?.id) ??
          list.find((i) => i.id === storedId) ??
          list[0] ??
          null
        );
      });
    } catch (e: unknown) {
      // 403 (нет скоупа read) — не ошибка для UI: модуль просто скрыт.
      if (e instanceof TikTokApiError && e.status === 403) {
        setInstances([]);
        setCurrentInstanceState(null);
      } else {
        setError(e instanceof Error ? e.message : 'Не удалось загрузить TikTok-каналы');
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, features.tiktok]);

  useEffect(() => {
    void refreshInstances();
  }, [refreshInstances]);

  const setCurrentInstance = useCallback((instance: TikTokInstanceOut | null) => {
    setCurrentInstanceState(instance);
    if (instance) {
      safeSetItem(STORAGE_KEY, String(instance.id));
    } else {
      safeRemoveItem(STORAGE_KEY);
    }
  }, []);

  const value = useMemo<TikTokContextType>(
    () => ({ instances, currentInstance, isLoading, error, setCurrentInstance, refreshInstances }),
    [instances, currentInstance, isLoading, error, setCurrentInstance, refreshInstances],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useTikTok = (): TikTokContextType => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTikTok must be used within TikTokProvider');
  return v;
};
